<template>
  <ModalComponent
    ref="modalRef"
    submitButtonText="Close"
    :hideSubmitButton="true"
    :dismissible="true"
    size="xl"
    @close="reset"
  >
    <template #title>
      <span v-if="selected"
        >NRR {{ periodLabel(selected.year, selected.month) }} ·
        {{ selected.window_months }}-month window</span
      >
    </template>
    <template #body>
      <div class="p-3">
        <LoadingIndicatorComponent
          v-if="isLoading"
          message="Loading customers..."
        />
        <div v-else-if="detail">
          <p class="text-muted small mb-3">
            Prior window <strong>{{ detail.prior_window_start }}</strong> →
            <strong>{{ dayBefore(detail.current_window_start) }}</strong> ·
            Current window <strong>{{ detail.current_window_start }}</strong> →
            <strong>{{ detail.current_window_end }}</strong>
            · Revenue = Soldera fee lines on paid fee invoices, by paid month.
          </p>

          <!-- Worked formula with this month's numbers, so the ratio can be checked by eye -->
          <div class="card bg-light border-0 mb-3">
            <div class="card-body py-2">
              <div
                class="d-flex justify-content-between align-items-start flex-wrap gap-2"
              >
                <h6 class="fw-bold mb-2">How these numbers are calculated</h6>
                <MethodologyModal title="NRR and GRR methodology">
                  <p>
                    For month <em>M</em> and window <em>W</em> (here
                    {{ detail.summary.window_months }} months):
                  </p>
                  <pre class="bg-light p-2 rounded small mb-3">
cohort  = companies with fee revenue in the prior window (M − 2W, M − W]
NRR(M)  = cohort revenue in the current window (M − W, M]  /  cohort revenue in the prior window
GRR(M)  = (prior − churn − contraction) / prior   ← each company capped at what it paid before</pre>
                  <dl>
                    <dt>Customer revenue</dt>
                    <dd>
                      Soldera fee lines (commission, cancellation, procurement,
                      issuance, transfer, flat and service fees) on paid fee
                      invoices, attributed to the invoiced company and the month
                      the invoice was paid. GO trading margin, referral payouts,
                      registry pass-through and buyer GO invoices are excluded.
                    </dd>
                    <dt>Expansion / contraction / churn</dt>
                    <dd>
                      Per cohort company: revenue above its prior-window revenue
                      is expansion, below is contraction, zero is churn. Prior +
                      expansion − contraction − churn = current, so the bridge
                      always reconciles.
                    </dd>
                    <dt>New customers</dt>
                    <dd>
                      No revenue in the prior window. Reported separately; never
                      part of NRR or GRR.
                    </dd>
                    <dt>Why a trailing window</dt>
                    <dd>
                      GO settlements are seasonal, so comparing single months
                      would show churn for a customer that simply settles in a
                      different quarter. 12 months smooths that; 6 and 3 react
                      faster.
                    </dd>
                  </dl>
                  <p class="mb-0 text-muted small">
                    Backend:
                    <code>app/money/admin/revenue_retention_service.py</code>.
                  </p>
                </MethodologyModal>
              </div>
              <pre
                class="mb-0 small formula"
              >NRR = current cohort revenue / prior cohort revenue
    = {{ formatMoney(detail.summary.current_revenue) }} / {{ formatMoney(detail.summary.prior_revenue) }}
    = <strong>{{ formatRatio(detail.summary.nrr) }}</strong>

GRR = (prior − churn − contraction) / prior
    = ({{ formatMoney(detail.summary.prior_revenue) }} − {{ formatMoney(detail.summary.churn) }} − {{ formatMoney(detail.summary.contraction) }}) / {{ formatMoney(detail.summary.prior_revenue) }}
    = <strong>{{ formatRatio(detail.summary.grr) }}</strong>

current = prior + expansion − contraction − churn
        = {{ formatMoney(detail.summary.prior_revenue) }} + {{ formatMoney(detail.summary.expansion) }} − {{ formatMoney(detail.summary.contraction) }} − {{ formatMoney(detail.summary.churn) }}
        = {{ formatMoney(detail.summary.prior_revenue + detail.summary.expansion - detail.summary.contraction - detail.summary.churn) }}
        <span :class="Math.abs(detail.summary.prior_revenue + detail.summary.expansion - detail.summary.contraction - detail.summary.churn - detail.summary.current_revenue) < 0.05 ? 'badge bg-success bg-opacity-25 text-success' : 'badge bg-danger'">
          {{ Math.abs(detail.summary.prior_revenue + detail.summary.expansion - detail.summary.contraction - detail.summary.churn - detail.summary.current_revenue) < 0.05 ? "✓ reconciles" : "≠ does not reconcile" }}
        </span></pre>
            </div>
          </div>

          <!-- Bridge -->
          <div class="row g-2 mb-4 text-center align-items-stretch">
            <div class="col">
              <div class="card h-100">
                <div class="card-body py-2">
                  <div class="small text-muted">Prior revenue</div>
                  <div class="fw-bold">
                    {{ formatMoney(detail.summary.prior_revenue) }} €
                  </div>
                  <div class="small text-muted">
                    {{ detail.summary.cohort_size }} customers
                  </div>
                </div>
              </div>
            </div>
            <div class="col">
              <div class="card h-100 border-success">
                <div class="card-body py-2">
                  <div class="small text-muted">+ Expansion</div>
                  <div class="fw-bold text-success">
                    {{ formatMoney(detail.summary.expansion) }} €
                  </div>
                  <div class="small text-muted">
                    {{ count("expanded") }} customers
                  </div>
                </div>
              </div>
            </div>
            <div class="col">
              <div class="card h-100 border-warning">
                <div class="card-body py-2">
                  <div class="small text-muted">− Contraction</div>
                  <div class="fw-bold text-warning-emphasis">
                    {{ formatMoney(detail.summary.contraction) }} €
                  </div>
                  <div class="small text-muted">
                    {{ count("contracted") }} customers
                  </div>
                </div>
              </div>
            </div>
            <div class="col">
              <div class="card h-100 border-danger">
                <div class="card-body py-2">
                  <div class="small text-muted">− Churn</div>
                  <div class="fw-bold text-danger">
                    {{ formatMoney(detail.summary.churn) }} €
                  </div>
                  <div class="small text-muted">
                    {{ detail.summary.churned_customers }} customers
                  </div>
                </div>
              </div>
            </div>
            <div class="col">
              <div class="card h-100 border-primary">
                <div class="card-body py-2">
                  <div class="small text-muted">= Current revenue</div>
                  <div class="fw-bold text-primary">
                    {{ formatMoney(detail.summary.current_revenue) }} €
                  </div>
                  <div class="small">
                    NRR <strong>{{ formatRatio(detail.summary.nrr) }}</strong> ·
                    GRR <strong>{{ formatRatio(detail.summary.grr) }}</strong>
                  </div>
                </div>
              </div>
            </div>
            <div class="col">
              <div class="card h-100 border-secondary">
                <div class="card-body py-2">
                  <div class="small text-muted">New (not in NRR)</div>
                  <div class="fw-bold text-secondary">
                    {{ formatMoney(detail.summary.new_revenue) }} €
                  </div>
                  <div class="small text-muted">
                    {{ detail.summary.new_customers }} customers
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex flex-wrap gap-2 mb-2 align-items-center">
            <span class="small text-muted me-1">Show:</span>
            <button
              v-for="status in STATUSES"
              :key="status"
              type="button"
              class="btn btn-sm"
              :class="
                visibleStatuses.has(status)
                  ? statusBtnClass[status]
                  : 'btn-outline-secondary'
              "
              @click="toggleStatus(status)"
            >
              {{ statusLabel[status] }} ({{ count(status) }})
            </button>
          </div>

          <DataTable
            :value="visibleCustomers"
            v-model:filters="filters"
            filterDisplay="row"
            sortField="prior_revenue"
            :sortOrder="-1"
            scrollable
            scrollHeight="480px"
            :virtualScrollerOptions="
              visibleCustomers.length > 30
                ? { itemSize: 42, numToleratedItems: 20 }
                : undefined
            "
            class="p-datatable-sm border rounded"
          >
            <Column
              field="company_name"
              header="Customer"
              sortable
              :showFilterMatchModes="false"
              :showFilterMenu="false"
            >
              <template #body="{ data }">
                <span class="badge text-bg-secondary me-1">{{
                  data.company_id
                }}</span>
                {{ data.company_name }}
                <span v-if="data.country" class="text-muted small ms-1">{{
                  data.country
                }}</span>
              </template>
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter w-100"
                  placeholder="Search"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="status"
              header="Status"
              sortable
              :style="{ width: '130px' }"
            >
              <template #body="{ data }">
                <span
                  class="badge"
                  :class="
                    statusBadgeClass[data.status as RetentionCustomerStatus]
                  "
                  >{{
                    statusLabel[data.status as RetentionCustomerStatus]
                  }}</span
                >
              </template>
            </Column>
            <Column
              field="prior_revenue"
              header="Prior"
              sortable
              :style="{ width: '140px' }"
            >
              <template #body="{ data }"
                >{{ formatMoney(data.prior_revenue) }} €</template
              >
            </Column>
            <Column
              field="current_revenue"
              header="Current"
              sortable
              :style="{ width: '140px' }"
            >
              <template #body="{ data }"
                >{{ formatMoney(data.current_revenue) }} €</template
              >
            </Column>
            <Column
              field="delta"
              header="Change"
              sortable
              :style="{ width: '160px' }"
            >
              <template #body="{ data }">
                <span
                  :class="
                    data.delta > 0
                      ? 'text-success'
                      : data.delta < 0
                        ? 'text-danger'
                        : 'text-muted'
                  "
                >
                  {{ data.delta > 0 ? "+" : "" }}{{ formatMoney(data.delta) }} €
                  <small v-if="data.prior_revenue > 0" class="text-muted"
                    >({{ data.delta >= 0 ? "+" : ""
                    }}{{ pct(data.delta, data.prior_revenue) }}%)</small
                  >
                </span>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </template>
    <template #footer>
      <button class="btn btn-secondary" @click="modalRef?.toggleModal()">
        Close
      </button>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import MethodologyModal from "./MethodologyModal.vue";
import { formatMoney } from "@/composables/formatUtils";
import { FilterMatchMode } from "@primevue/core/api";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import { computed, ref, useTemplateRef } from "vue";
import { financialDashboardApi } from "./financialDashboardApi";
import { formatRatio, pct, periodLabel } from "./financialDashboardMetrics";
import type {
  RetentionCustomerStatus,
  RetentionDetail,
  RetentionRow,
} from "./financialDashboardTypes";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

/**
 * Per-customer bridge behind one month's NRR. Owns its own fetch; open it with `open(row)`.
 * `countryCodes` must match the filter that produced the clicked series.
 */
const props = defineProps<{
  countryCodes: string[];
}>();

const STATUSES: RetentionCustomerStatus[] = [
  "expanded",
  "retained",
  "contracted",
  "churned",
  "new",
];

const statusLabel: Record<RetentionCustomerStatus, string> = {
  expanded: "Expanded",
  retained: "Retained",
  contracted: "Contracted",
  churned: "Churned",
  new: "New",
};

const statusBadgeClass: Record<RetentionCustomerStatus, string> = {
  expanded: "bg-success",
  retained: "bg-primary",
  contracted: "bg-warning text-dark",
  churned: "bg-danger",
  new: "bg-secondary",
};

const statusBtnClass: Record<RetentionCustomerStatus, string> = {
  expanded: "btn-success",
  retained: "btn-primary",
  contracted: "btn-warning",
  churned: "btn-danger",
  new: "btn-secondary",
};

const { globalErrorHandler } = useGlobalHandlers();

const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");
const selected = ref<RetentionRow | null>(null);
const detail = ref<RetentionDetail | null>(null);
const isLoading = ref(false);
const visibleStatuses = ref<Set<RetentionCustomerStatus>>(new Set(STATUSES));
const filters = ref({
  company_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const visibleCustomers = computed(() =>
  (detail.value?.customers ?? []).filter((customer) =>
    visibleStatuses.value.has(customer.status)
  )
);

const count = (status: RetentionCustomerStatus): number =>
  (detail.value?.customers ?? []).filter(
    (customer) => customer.status === status
  ).length;

const dayBefore = (isoDate: string): string => {
  const date = new Date(isoDate);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().split("T")[0];
};

function toggleStatus(status: RetentionCustomerStatus) {
  const next = new Set(visibleStatuses.value);
  if (next.has(status)) next.delete(status);
  else next.add(status);
  visibleStatuses.value = next;
}

// Sequence number so a slow response for an earlier selection cannot overwrite the current one.
let requestSeq = 0;

/** Open for a month; `statuses` preselects the status filter (e.g. only churned customers). */
async function open(row: RetentionRow, statuses?: RetentionCustomerStatus[]) {
  const request = ++requestSeq;
  selected.value = row;
  detail.value = null;
  visibleStatuses.value = new Set(statuses ?? STATUSES);
  modalRef.value?.toggleModal();
  isLoading.value = true;
  try {
    const response = await financialDashboardApi.getRetentionDetail(
      row.year,
      row.month,
      row.window_months,
      props.countryCodes
    );
    if (request !== requestSeq) return;
    detail.value = response.data;
  } catch (error) {
    if (request === requestSeq) globalErrorHandler(error);
  } finally {
    if (request === requestSeq) isLoading.value = false;
  }
}

function reset() {
  selected.value = null;
  detail.value = null;
  visibleStatuses.value = new Set(STATUSES);
}

defineExpose({ open });
</script>

<style scoped lang="scss">
.formula {
  white-space: pre-wrap;
  font-family: var(--bs-font-monospace);
}
</style>
